// GoogleAdsIntegration.js
import React, { useEffect, useState } from 'react';
import { getAuthToken, fetchAccessibleCustomers, searchCustomerAccounts, fetchCredentials, getGoogleAdsCampaignList, getGoogleAdsErrorMessage } from '../../../../services/ApiService';
import { getOAuthRedirectUri } from '../../../../utils/oauthRedirect';
import { toast } from 'react-toastify';
import AdsCard from '../AdsCard';
import google_ads_icon from '../../../../assests/google_ads.svg';
import { CommonModal } from '../../../CommonModal';
import { TableAdsAccounts } from './TableAdsAccounts';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import UnassociatedCampainModal from './UnassociatedCampainModal';
import { UPDATE_USER_DATA } from '../../../../redux/features/user/userSlice';

const googleOAuthDevLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('[Google OAuth Dev]', ...args);
  }
};

const getAccessibleCustomerResourceNames = (accessibleCustomers) => {
  if (!accessibleCustomers || typeof accessibleCustomers !== 'object') {
    return [];
  }

  const names = accessibleCustomers.resourceNames ?? accessibleCustomers?.data?.resourceNames;
  return Array.isArray(names) ? names : [];
};

const flattenCustomerAccountResults = (customerData) => {
  if (!Array.isArray(customerData)) {
    return [];
  }

  return customerData.flatMap((chunk) => {
    const results = Array.isArray(chunk?.results) ? chunk.results : [];
    return results
      .map((item) => item?.customer)
      .filter(Boolean);
  });
};

const GoogleAdsIntegration = ({ isAccountLinked ,fetchAdsAccountStatus,accountDetails,social_media_type}) => {
  const [authCode, setAuthCode] = useState("");
  const [apiData, setApiData] = useState([]);
  // const [accessibleCustomersData, setAccessibleCustomersData] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [showAdsAccountsModal, setShowAdsAccountsModal] = useState(false);
  const [connecting, setConnecting] = useState(false);


  const [loadingUnAssociatedCampaigns, setLoadingUnAssociatedCampaigns] = useState(false);
  const [campaignList, setCampaignList] = useState([])
  const [showUnassociatedCampainModal, setShowUnassociatedCampainModal] = useState(false)


  const user = useSelector((state) => state?.user);
  const dispatch = useDispatch();


  const location = useLocation();
  const platformId = location.state?.platform_id;

  const handleConnect = async () => {
    setConnecting(true);
    localStorage.setItem('selected_platform_to_connect', 'google');
    localStorage.removeItem('google_code');
    googleOAuthDevLog('Connect clicked', {
      redirect_uri: getOAuthRedirectUri(),
    });

    const credentials = await fetchCredentials();
    googleOAuthDevLog('google_ads_env_variables loaded', {
      has_google: Boolean(credentials?.google),
      client_id: credentials?.google?.google_ads_client_id,
    });


    if (credentials?.google?.google_ads_client_id) {
 
      dispatch(
        UPDATE_USER_DATA({
          CLIENT_ID:  credentials.google.google_ads_client_id,
        })
      );

      const API_URL1 = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${credentials.google.google_ads_client_id}&redirect_uri=${encodeURIComponent(getOAuthRedirectUri())}&response_type=code&scope=https://www.googleapis.com/auth/adwords&access_type=offline&prompt=consent`;
      window.location.href = API_URL1;
    } else {
      setConnecting(false);
      toast.error('Error connecting to Google Ads');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");
    const errorDescription = params.get("error_description");
    const selectedPlatform = localStorage.getItem('selected_platform_to_connect');
    googleOAuthDevLog('OAuth callback check', {
      has_code: Boolean(code),
      has_error: Boolean(error),
      selected_platform_to_connect: selectedPlatform,
    });

    if (error && selectedPlatform === 'google') {
      toast.error(errorDescription || `Google authorization failed. Add this redirect URL in Google Cloud Console: ${getOAuthRedirectUri()}`);
      setConnecting(false);
      return;
    }

    if (code && selectedPlatform === 'google') {
      setAuthCode(code);
    }
  }, []);

  useEffect(() => {
    const fetchGoogleApi = async () => {
      if (authCode) {
        setConnecting(true);
        googleOAuthDevLog('Processing auth code', {
          code_prefix: `${authCode?.slice(0, 12)}...`,
          redirect_uri: getOAuthRedirectUri(),
        });
        try {
          const data = await getAuthToken(authCode);
          googleOAuthDevLog('Google token exchange result', {
            success: Boolean(data?.success),
          });
          setApiData(data);

          const accessibleCustomers = await fetchAccessibleCustomers();
          googleOAuthDevLog('listAccessibleCustomers result', accessibleCustomers);
          const resourceNames = getAccessibleCustomerResourceNames(accessibleCustomers);

          if (resourceNames.length > 0) {
            const customerData = [];

            for (const resourceName of resourceNames) {
              if (!resourceName || typeof resourceName !== 'string') {
                continue;
              }

              try {
                const customerId = resourceName.split('/')[1];
                if (!customerId) {
                  continue;
                }

                const customerAccountsResponse = await searchCustomerAccounts(customerId);
                if (Array.isArray(customerAccountsResponse)) {
                  customerData.push(...customerAccountsResponse);
                } else if (customerAccountsResponse) {
                  customerData.push(customerAccountsResponse);
                }
              } catch (error) {
                console.error(`Error fetching data for customer ID ${resourceName}:`, error);
              }
            }

            const flattenedCustomerAccounts = flattenCustomerAccountResults(customerData);
            setCustomerAccounts(flattenedCustomerAccounts);

            if (flattenedCustomerAccounts.length > 0) {
              googleOAuthDevLog('Opening ad accounts modal', {
                accounts_count: flattenedCustomerAccounts.length,
              });
              setShowAdsAccountsModal(true);
            } else {
              googleOAuthDevLog('No Google ad accounts found');
              toast.error("You do not have any Google Ads Account associated with your Google ID.");
            }
          } else {
            googleOAuthDevLog('No accessible Google Ads customers returned');
            toast.error("No Google Ads accounts were found for this Google account.");
          }
        } catch (error) {
          console.error("Error fetching token or customer data:", error);
          toast.error(getGoogleAdsErrorMessage(error));
          googleOAuthDevLog('Google connect failed', error);
        } finally {
          setConnecting(false);
        }
      }
    };
   if(localStorage.getItem('selected_platform_to_connect')==='google')
   {
    fetchGoogleApi();
   }
  }, [authCode]);
  



  // get unasociated campains

  const handleShowUnAssociatedCampaigns = async () => {
    if(isAccountLinked){
      setLoadingUnAssociatedCampaigns(true); // Set loading state to true
      try {
        const result = await getGoogleAdsCampaignList();
  
        if (result?.campaigns?.length > 0) {
          setCampaignList(result?.campaigns);
          setShowUnassociatedCampainModal(true); // Show the modal after data is fetched
        } else {
          setCampaignList([]);
          toast?.error(
            "No new campaigns were found. Please ensure that campaigns have been created or try again later."
          );
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
      } finally {
        setLoadingUnAssociatedCampaigns(false); // Reset loading state
      }
    }
  };

  // useEffect(() => {
  //   if(isAccountLinked && platformId===1){
  //     handleShowUnAssociatedCampaigns()
  //   }
  // }, [location]);

 
  return (
    <>
      <AdsCard
        handleConnect={handleConnect}
        isAccountLinked={isAccountLinked}
        platform={"Google Ads"}
        platform_icon={google_ads_icon}
        connecting={connecting}
        description="Google Ads is Google's online advertising platform that enables businesses to create and manage targeted advertising campaigns across various Google services."
        handleShowUnAssociatedCampaigns={handleShowUnAssociatedCampaigns}
        loadingUnAssociatedCampaigns={loadingUnAssociatedCampaigns}
        accountDetails={accountDetails}
        social_media_type={social_media_type}
        fetchAdsAccountStatus={fetchAdsAccountStatus}
      />

      {connecting ? null : (
        <CommonModal
          title={
            <>
              <div className="d-flex flex-column gap-1">
                <span className="fs-4 fw-bold">Your Google Ads Accounts</span>
                <small className="fs-8">Select an account to proceed with integration.</small>
              </div>
            </>
          }
          body={
            customerAccounts.length > 0 && (
              <TableAdsAccounts
                fetchAdsAccountStatus={fetchAdsAccountStatus}
                customerAccounts={customerAccounts}
                apiData={apiData}
                setCustomerAccounts={setCustomerAccounts}
                setShowAdsAccountsModal={setShowAdsAccountsModal}
              />
            )
          }
          show={showAdsAccountsModal}
          setShow={setShowAdsAccountsModal}
        />
      )}

      {showUnassociatedCampainModal && (
        <UnassociatedCampainModal
          showUnassociatedCampainModal={showUnassociatedCampainModal}
          setShowUnassociatedCampainModal={setShowUnassociatedCampainModal}
          campaignList={campaignList}
        />
      )}

    </>
  );
};

export default GoogleAdsIntegration;
